import { apiJson } from '@/lib/api';
import { apiRequest } from '@/lib/queryClient';
import type {
  PartnerAuditEventRow,
  PartnerMembershipRow,
  PartnerOrganisationDetail,
  PartnerOrganisationSummary,
  PartnerPortalPermissionRow,
  PartnerPortalRoleRow,
  PartnerRetailerGrantRow,
  PartnerUserSearchResult,
} from '@shared/partner-access-admin-types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

function requireOnlineForProvisioning(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error(
      'Partner access changes require an internet connection. Reconnect and try again.',
    );
  }
}

async function provisioningJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  requireOnlineForProvisioning();
  return apiJson<T>(path, init);
}

export const PartnerAccessService = {
  listOrganisations: async () => {
    const res = await apiRequest('/api/admin/partner-access/organisations', 'GET');
    return res.json() as Promise<ApiResponse<{ organisations: PartnerOrganisationSummary[] }>>;
  },

  getOrganisation: async (organisationId: number) => {
    const res = await apiRequest(`/api/admin/partner-access/organisations/${organisationId}`, 'GET');
    return res.json() as Promise<ApiResponse<{ organisation: PartnerOrganisationDetail }>>;
  },

  createOrganisation: async (input: { key: string; name: string }) => {
    return provisioningJson<ApiResponse<{ organisation: PartnerOrganisationDetail }>>(
      '/api/admin/partner-access/organisations',
      { method: 'POST', body: JSON.stringify(input) },
    );
  },

  updateOrganisation: async (
    organisationId: number,
    input: { name?: string; status?: 'active' | 'inactive' },
  ) => {
    return provisioningJson<ApiResponse<{ organisation: PartnerOrganisationDetail }>>(
      `/api/admin/partner-access/organisations/${organisationId}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    );
  },

  listGrants: async (organisationId: number) => {
    const res = await apiRequest(
      `/api/admin/partner-access/organisations/${organisationId}/grants`,
      'GET',
    );
    return res.json() as Promise<ApiResponse<{ grants: PartnerRetailerGrantRow[] }>>;
  },

  addGrant: async (organisationId: number, retailerId: number) => {
    return provisioningJson<ApiResponse<{ grant: PartnerRetailerGrantRow }>>(
      `/api/admin/partner-access/organisations/${organisationId}/grants`,
      { method: 'POST', body: JSON.stringify({ retailerId }) },
    );
  },

  setGrantStatus: async (
    organisationId: number,
    grantId: number,
    status: 'active' | 'revoked',
  ) => {
    return provisioningJson<ApiResponse<{ grant: PartnerRetailerGrantRow }>>(
      `/api/admin/partner-access/organisations/${organisationId}/grants/${grantId}`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
    );
  },

  listMemberships: async (organisationId: number) => {
    const res = await apiRequest(
      `/api/admin/partner-access/organisations/${organisationId}/memberships`,
      'GET',
    );
    return res.json() as Promise<ApiResponse<{ memberships: PartnerMembershipRow[] }>>;
  },

  addMembership: async (organisationId: number, userId: number) => {
    return provisioningJson<ApiResponse<{ membership: PartnerMembershipRow }>>(
      `/api/admin/partner-access/organisations/${organisationId}/memberships`,
      { method: 'POST', body: JSON.stringify({ userId }) },
    );
  },

  setMembershipStatus: async (membershipId: number, status: 'active' | 'revoked') => {
    return provisioningJson<ApiResponse<{ membership: PartnerMembershipRow }>>(
      `/api/admin/partner-access/memberships/${membershipId}`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
    );
  },

  listAuditEvents: async (organisationId: number, limit = 50) => {
    const res = await apiRequest(
      `/api/admin/partner-access/organisations/${organisationId}/audit?limit=${limit}`,
      'GET',
    );
    return res.json() as Promise<ApiResponse<{ events: PartnerAuditEventRow[] }>>;
  },

  listRoles: async () => {
    const res = await apiRequest('/api/admin/partner-access/roles', 'GET');
    return res.json() as Promise<ApiResponse<{ roles: PartnerPortalRoleRow[] }>>;
  },

  listPermissions: async () => {
    const res = await apiRequest('/api/admin/partner-access/permissions', 'GET');
    return res.json() as Promise<ApiResponse<{ permissions: PartnerPortalPermissionRow[] }>>;
  },

  searchUsers: async (query: string) => {
    const params = new URLSearchParams({ q: query });
    const res = await apiRequest(`/api/admin/partner-access/users/search?${params.toString()}`, 'GET');
    return res.json() as Promise<ApiResponse<{ users: PartnerUserSearchResult[] }>>;
  },
};
